import React from "react";
import { FaTimes } from "react-icons/fa";
import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi";

import { cn } from "../../../ui";

type Props = {
  error?: boolean | null;
  success?: boolean | null;
  message?: string | null;
  onDismiss?: () => void;
};

const MessageBanner: React.FC<Props> = ({
  error,
  success,
  message,
  onDismiss,
}) => {
  if (!error && !success) return null;
  if (!message) return null;

  const isSuccess = Boolean(success);
  const tone = isSuccess
    ? {
        bg: "bg-emerald-50 border-emerald-200",
        title: "text-emerald-800",
        body: "text-emerald-700",
        icon: <HiCheckCircle className="h-5 w-5 text-emerald-600" />,
        label: "Success",
        aria: "polite" as const,
      }
    : {
        bg: "bg-red-50 border-red-200",
        title: "text-red-800",
        body: "text-red-700",
        icon: <HiExclamationCircle className="h-5 w-5 text-red-600" />,
        label: "Error",
        aria: "assertive" as const,
      };

  return (
    <div
      role="alert"
      aria-live={tone.aria}
      className={cn(
        "flex w-full items-start gap-3 rounded-xl border px-4 py-3",
        tone.bg,
      )}
    >
      <div className="mt-0.5">{tone.icon}</div>
      <div className="min-w-0 flex-1">
        <p className={cn("text-[11px] font-semibold uppercase tracking-wide", tone.title)}>
          {tone.label}
        </p>
        <p className={cn("mt-0.5 text-[13px] leading-5", tone.body)}>{message}</p>
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss message"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-white/60 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25"
        >
          <FaTimes className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
};

export default MessageBanner;
